import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Check, Star, Zap, Building, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

const planIcons = { free: CreditCard, pro: Zap, enterprise: Building };
const planColors = {
  free: { border: 'border-stone-200', btn: 'bg-stone-800 hover:bg-stone-900', badge: '' },
  pro: { border: 'border-[#064E3B]', btn: 'bg-[#064E3B] hover:bg-[#022C22]', badge: 'bg-[#064E3B] text-white' },
  enterprise: { border: 'border-amber-300', btn: 'bg-amber-600 hover:bg-amber-700', badge: 'bg-amber-100 text-amber-800' },
};

export default function Plans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [plansRes, currentRes] = await Promise.all([
          api.get('/plans'),
          api.get('/plans/current'),
        ]);
        setPlans(plansRes.data);
        setCurrentPlan(currentRes.data);
      } catch {
        toast.error('Erro ao carregar planos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const { data } = await api.post(`/plans/checkout/${planId}`);
      if (data.checkout_url) {
        window.open(data.checkout_url, '_blank');
      } else {
        toast.info(data.message || 'Entre em contato para assinar.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const isCurrent = (planId) => user?.plano === planId;

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content p-6 lg:p-8 pt-16 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1
              className="text-3xl font-bold text-stone-900 mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Escolha seu Plano
            </h1>
            <p className="text-stone-500">
              Expanda sua capacidade e venda mais imóveis com as ferramentas certas.
            </p>
          </div>

          {/* Usage indicator */}
          {currentPlan && (
            <div className="bg-[#064E3B]/5 border border-[#064E3B]/20 rounded-lg p-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#064E3B]">
                  Plano atual: <span className="capitalize">{currentPlan.nome}</span>
                </p>
                <p className="text-xs text-stone-500 mt-0.5">
                  {currentPlan.simulacoes_usadas_mes ?? 0} simulações usadas este mês
                  {currentPlan.limite_simulacoes !== -1 ? ` de ${currentPlan.limite_simulacoes}` : ' (ilimitadas)'}
                </p>
              </div>
              {currentPlan.limite_simulacoes !== -1 && (
                <div className="w-full sm:w-48">
                  <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#064E3B] rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, ((currentPlan.simulacoes_usadas_mes ?? 0) / currentPlan.limite_simulacoes) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Plans */}
          {loading ? (
            <div className="text-center py-16 text-stone-400">Carregando planos...</div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const colors = planColors[plan.id] || planColors.free;
                const PlanIcon = planIcons[plan.id] || CreditCard;
                const current = isCurrent(plan.id);

                return (
                  <div
                    key={plan.id}
                    data-testid={`plan-card-${plan.id}`}
                    className={`bg-white border-2 ${colors.border} rounded-xl p-6 relative ${plan.destaque ? 'shadow-lg' : ''} transition-all`}
                  >
                    {plan.destaque && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-[#064E3B] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Star size={11} className="fill-white" /> Mais Popular
                        </span>
                      </div>
                    )}

                    <div className="mb-5">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${plan.id === 'enterprise' ? 'bg-amber-50' : 'bg-[#064E3B]/10'}`}>
                        <PlanIcon size={20} className={plan.id === 'enterprise' ? 'text-amber-600' : 'text-[#064E3B]'} />
                      </div>
                      <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {plan.nome}
                      </h3>
                      <p className="text-xs text-stone-500 mt-1">{plan.descricao}</p>
                    </div>

                    <div className="mb-5">
                      {plan.preco_mensal === 0 ? (
                        <p className="text-3xl font-bold text-stone-900">Grátis</p>
                      ) : (
                        <div>
                          <p className="text-3xl font-bold text-stone-900">
                            {formatCurrency(plan.preco_mensal)}
                            <span className="text-sm font-normal text-stone-400">/mês</span>
                          </p>
                          <p className="text-xs text-stone-400 mt-0.5">
                            ou {formatCurrency(plan.preco_anual)}/ano (2 meses grátis)
                          </p>
                        </div>
                      )}
                    </div>

                    <ul className="space-y-2 mb-6">
                      {plan.features?.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-stone-600">
                          <Check size={14} className="text-[#064E3B] mt-0.5 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    {current ? (
                      <div className="w-full py-2 text-center text-sm font-semibold text-[#064E3B] bg-[#064E3B]/10 rounded-lg">
                        Plano Atual
                      </div>
                    ) : (
                      <Button
                        data-testid={`btn-upgrade-${plan.id}`}
                        onClick={() => handleCheckout(plan.id)}
                        disabled={checkoutLoading === plan.id || plan.preco_mensal === 0}
                        className={`w-full ${colors.btn} text-white font-semibold`}
                      >
                        {checkoutLoading === plan.id ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : plan.preco_mensal === 0 ? (
                          'Plano Básico'
                        ) : (
                          `Assinar ${plan.nome}`
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-center text-xs text-stone-400 mt-8">
            Todos os planos incluem acesso completo à Calculadora TesIQ.
            Pagamentos processados com segurança.
          </p>
        </div>
      </main>
    </div>
  );
}
